import React, { useEffect } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme, useScrollspy } from '@sg/garnish';
import {
  CustomizationIngredientsGrid,
  type CustomizationMachineActorRef,
  type CustomizationMachineTypes,
  useCustomizationContext,
  useCustomizationHelpers,
  useCustomizationStepperTimeout,
} from '@sg/shared-fe-features-customization';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useModificationGroupHelperText } from '../../../../hooks';
import { CustomizationModificationGroup } from '../CustomizationModificationGroup';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationIngredientsGridPanel = (props: Props) => {
  const { actor, activeTargetId, modificationGroups } = props;

  const { bottom: safeAreaBottomInset } = useSafeAreaInsets();

  const getSectionHelperText = useModificationGroupHelperText();
  const { activeStepperId, setActiveStepperId, resetStepperTimeout, clearStepperTimeout } = useCustomizationStepperTimeout({ resetOnWindowBlur: true }); // prettier-ignore
  const isModificationGroupHelperTextEnabled = useFeatureFlag('CELS-4762-modification-group-helper-text-enabled'); // prettier-ignore

  const scrollSpy = useScrollspy({
    defaultTargetId: activeTargetId ?? undefined,
  });
  const { scrollViewRef, scrollTo, onScrollViewContentSizeChange, onScrollViewLayout } = scrollSpy;

  const { modificationLimits } = useCustomizationContext({
    actor,
  });

  const { getFormattedQuantity } = useCustomizationHelpers({ actor });

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!activeTargetId) return;

    scrollTo(activeTargetId);
  }, [activeTargetId, scrollTo]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const contentContainerDynamicStyles = useStyle(() => {
    const bottomInset = Platform.select({ android: safeAreaBottomInset, default: 0 });

    return {
      paddingBottom: theme.spacing['30'] + bottomInset,
    };
  }, [safeAreaBottomInset]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <CustomizationIngredientsGrid.Container
      ref={scrollViewRef}
      containerStyle={styles.ingredientsGridContainer}
      contentContainerStyle={contentContainerDynamicStyles}
      onContentSizeChange={onScrollViewContentSizeChange}
      onLayout={onScrollViewLayout}
    >
      {modificationGroups?.map((group) => {
        const freeAggregateQuantity = modificationLimits[group.name]?.freeAggregateQuantity;

        const customHelperText = getSectionHelperText({
          freeAggregateQuantity,
          ingredientKind: group.modificationIngredientKind,
        });
        const helperText = isModificationGroupHelperTextEnabled
          ? group.helperText
          : customHelperText;

        return (
          <CustomizationModificationGroup
            key={group.id}
            actor={actor}
            helperText={helperText}
            formattedQuantity={getFormattedQuantity(group.name)}
            modificationGroup={group}
            activeStepperId={activeStepperId}
            resetStepperTimeout={resetStepperTimeout}
            clearStepperTimeout={clearStepperTimeout}
            setActiveStepperId={setActiveStepperId}
            scrollSpy={scrollSpy}
          />
        );
      })}
    </CustomizationIngredientsGrid.Container>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ingredientsGridContainer: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  actor: CustomizationMachineActorRef;
  activeTargetId: string | null | undefined;
  modificationGroups: CustomizationMachineTypes.ExtendedModifierGroup[] | undefined;
};
